import {
    DEVICE_ANDROID,
    DEVICE_IOS,
} from '../../consts'
import { getUrlParameter } from './get-url-parameter'

export const EVENT_REGISTRATION: string = 'registration'
export const EVENT_OTHER: string = 'other'

export const EVENT_TYPE_DEFAULT: number = 0
export const EVENT_TYPE_REDIRECT: number = 1

export interface WebViewData {
    close: boolean
    data: unknown
    type: number
    action: string | null
}

/**
 * Get default data
 */
export const defaultData: WebViewData = {
    close: false,
    data: null,
    type: EVENT_TYPE_DEFAULT,
    action: null,
}

/**
 * Get user navigator device or returns false if is not defined
 */
export const navigatorDevice: string | false =
    ((navigator.userAgent.match(/(device-)(ios|android)/)) || [])[2] || false

/**
 * Check if url is web viewed
 *
 * @returns {boolean}
 */
export const isWebView = (): boolean =>
    navigatorDevice !== false || getUrlParameter('webview') === 'true'

/**
 * Send data to webview if webview exists
 *
 * @param {string} event
 * @param {Partial<WebViewData>|null} data
 * @return {false|Promise<WebViewData>}
 */
export const sendWebViewEvent = (
    event: string,
    data: Partial<WebViewData> | null = null,
): false | Promise<WebViewData> => {
    if (!navigatorDevice) {
        return false
    }

    const payload: WebViewData = data === null
        ? { ...defaultData }
        : { ...defaultData, ...data }

    if (navigatorDevice === DEVICE_IOS && window && (window as Window & typeof globalThis & { webkit?: { messageHandlers: Record<string, { postMessage: (data: unknown) => void } > } }).webkit) {
        ;(window as Window & typeof globalThis & { webkit: { messageHandlers: Record<string, { postMessage: (data: unknown) => void }> } }).webkit.messageHandlers[event].postMessage(payload)
        return new Promise<WebViewData>(resolve => resolve(payload))
    }

    if (navigatorDevice === DEVICE_ANDROID && window && (window as unknown as Record<string, { postMessage: (data: string) => void }>)[event]) {
        ;(window as unknown as Record<string, { postMessage: (data: string) => void }>)[event].postMessage(JSON.stringify(payload))
        return new Promise<WebViewData>(resolve => resolve(payload))
    }

    return false
}

/**
 * Try send webview event, or redirect out
 *
 * @param {string} link
 * @param {string|null} target
 * @param {boolean} redirectAfterFail - redirect after fail sending webview event?
 * @return {boolean}
 */
export const redirectWithEvents = (
    link: string,
    target: string | null = null,
    redirectAfterFail: boolean = true,
): boolean => {
    const eventData: Partial<WebViewData> = {
        type: EVENT_TYPE_REDIRECT,
        action: link,
    }

    if (sendWebViewEvent(EVENT_OTHER, eventData) === false) {
        if (redirectAfterFail) {
            if (target === '_blank') {
                window.open(link, '_blank')
            } else {
                window.location.href = link
            }
        }

        return true
    }

    return false
}

/**
 * Replace all links inside container with event click
 *
 * @param {HTMLElement|null} container
 */
export const setEventsForLinks = (container: HTMLElement | null): void => {
    if (isWebView() && container) {
        const links = container.getElementsByTagName('a')

        if (links.length > 0) {
            Object.values(links).forEach(link => {
                link.onclick = (evt: PointerEvent) => {
                    evt.preventDefault()
                    redirectWithEvents(link.href, '_blank')
                }
            })
        }
    }
}

/**
 * Check if webview is Android
 */
export const isAndroidWebView: boolean = navigatorDevice === DEVICE_ANDROID

/**
 * Check if webview is iOS
 */
export const isiOSWebView: boolean = navigatorDevice === DEVICE_IOS
